import React, { useContext, useState, useMemo, useRef } from "react";
import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";

import WelcomeCard from "../../components/WelcomeCard";
import StatsGrid from "../../components/StatsGrid";
import * as icons from "../../constant/icons";
import { useEmployees } from "./hooks/useEmployees";
import { useEmployeeProfilePics } from "./hooks/useEmployeeProfilePics";
import EmptyCard from "../../components/EmptyCard";
import Button from "../../components/ConfigurableButton";
import AlertModal from "../../components/AlertModal";
import { Plus, Upload, Download, FileText, Archive } from "lucide-react";
import AddEmployeeModal from "./components/modals/AddEmployeeModal";
import DocumentsModal from "./components/modals/DocumentsModal";
import ViewEmployeeModal from "./components/modals/ViewEmployeeModal";
import EditEmployeeModal from "./components/modals/EditEmployeeModal";
import {
  addEmployeeAPI,
  mapFormDataToDatabase,
} from "./services/employeeService";
import { useEmployeeDocuments } from "./hooks/useEmployeeDocuments";
import { apiFetch } from "../../context/AuthContext";
import ReusableDropdown, {
  unifiedControlStyle,
} from "../../components/ReusableDropdown";
import EmployeeTable from "./components/EmployeeTable";
import ImportPreviewModal from "./components/modals/ImportPreviewModal";
import { ImportResultsModal } from "./components/modals/ImportResultsModal";
import { useEmployeeImportFlow } from "./hooks/useEmployeeImportFlow";
import { useEmployeeExport } from "./hooks/useEmployeeExport";
import { useArchivedEmployees } from "./hooks/useArchivedEmployees";
import ArchiveModal from "./components/modals/ArchiveModal";
import ExportTemplateModal from "../../components/ExportTemplateModal";

import { text } from "./constants/text";
import { getResponsiveStyles } from "../../utils/styleUtils";
import { useProfile } from "../../hooks/useProfile";

// Import template file
import templateFile from "../../Documents/Employees_template.xlsx";
const Dashboard = ({
  employeeCount: propEmployeeCount,
  counts: propCounts,
  departmentStats: propDepartmentStats,
} = {}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();

  // derive a responsive screen size key for styles (safe on server)
  const screenSize =
    typeof window !== "undefined"
      ? window.innerWidth <= 480
        ? "xs"
        : window.innerWidth <= 768
          ? "sm"
          : window.innerWidth <= 1024
            ? "md"
            : window.innerWidth <= 1366
              ? "lg"
              : window.innerWidth <= 1920
                ? "xl"
                : "xxl"
      : "lg";

  const styles =
    typeof getResponsiveStyles === "function"
      ? getResponsiveStyles(screenSize, theme, language)
      : {};

  // derive values from props or hook (safe defaults)
  const {
    employeeCount: hookEmployeeCount,
    counts: hookCounts,

    fetchAll: refetchEmployees,
    companies,
    employees: hookEmployees,
    setEmployees,
    loading: employeesLoading,
    clientsMap,
    clientsList,
  } = useEmployees();

  // Get employee profile pictures
  const { employeeProfilePics } = useEmployeeProfilePics(hookEmployees);

  // Memoize unique department names derived from employees (match EmployeeTable.js behavior)
  const departmentsList = useMemo(() => {
    try {
      const list = Array.isArray(hookEmployees)
        ? hookEmployees.map((e) => e?.department || "").filter(Boolean)
        : [];
      return Array.from(new Set(list)).sort();
    } catch (err) {
      return [];
    }
  }, [hookEmployees]);

  const companiesList = useMemo(() => {
    try {
      const list = Array.isArray(hookEmployees)
        ? hookEmployees
            .map((e) => e?.company || e?.workingCompany || e?.clientName || "")
            .filter(Boolean)
        : [];
      return Array.from(new Set(list)).sort();
    } catch (err) {
      return [];
    }
  }, [hookEmployees]);

  const employeeCount = propEmployeeCount ?? hookEmployeeCount ?? 0;
  // Merge hookCounts with propCounts so partial propCounts don't drop keys like `archived` or `newEmployees`
  const counts = {
    Active: 0,
    OnLeave: 0,
    archived: 0,
    newEmployees: 0,
    Total: 0,
    ...(hookCounts || {}),
    ...(propCounts || {}),
  };

  // Import flow state
  const {
    pendingImportFile,
    setPendingImportFile,
    importPreviewOpen,
    setImportPreviewOpen,
    importPreviewData,
    setImportPreviewData,
    duplicateDecisions,
    setDuplicateDecisions,
    previewStep,
    setPreviewStep,
    expandedDupKeys,
    setExpandedDupKeys,
    isPreparingImport,
    setIsPreparingImport,
    isImportCommitting,
    setIsImportCommitting,
    importResultsOpen,
    setImportResultsOpen,
    importResultsData,
    setImportResultsData,
  } = useEmployeeImportFlow();

  // Export flow state
  const { isExporting, handleExport } = useEmployeeExport();

  // Archive flow state
  const {
    archivedEmployees,
    archivedCount,
    loading: archiveLoading,
    archiveSearch,
    setArchiveSearch,
    archiveFilterCompany,
    setArchiveFilterCompany,
    archiveFilterCompanySearch,
    setArchiveFilterCompanySearch,
    archiveFilterWorkingCompany,
    setArchiveFilterWorkingCompany,
    archiveFilterWorkingCompanySearch,
    setArchiveFilterWorkingCompanySearch,
    archiveFilterDepartment,
    setArchiveFilterDepartment,
    archiveFilterDepartmentSearch,
    setArchiveFilterDepartmentSearch,
    archiveFilterDateFrom,
    setArchiveFilterDateFrom,
    archiveFilterDateTo,
    setArchiveFilterDateTo,
    filteredArchivedEmployees,
    filterOptions,
    archivedThisMonth,
    fetchArchivedEmployees,
    restoreEmployee,
    restoreAllEmployees,
    deleteAllArchived,
    clearFilters: clearArchiveFilters,
  } = useArchivedEmployees();

  const [showArchive, setShowArchive] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState(null);

  const fileInputRef = useRef(null);

  // Documents modal state
  const {
    showDocumentsModal,
    setShowDocumentsModal,
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,
    loadingDocuments,
    savingDocument,
    hasUnsavedChanges,
    documentChecklist,
    loadEmployeeDocuments,
    updateDocumentCheckbox,
    saveDocuments,
  } = useEmployeeDocuments();

  // Add employee modal state
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });

  // Edit employee modal state
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Export template modal state
  const [showExportTemplate, setShowExportTemplate] = useState(false);
  const [exportTemplateLoading, setExportTemplateLoading] = useState(false);

  const showAlert = ({ message, title, variant = "info" }) =>
    new Promise((resolve) => {
      setAlertState({
        open: true,
        title: title || "",
        message,
        variant,
        showCancel: false,
        onResolve: resolve,
      });
    });

  const showConfirm = ({ message, title = "", variant = "error" } = {}) =>
    new Promise((resolve) => {
      setAlertState({
        open: true,
        title,
        message,
        variant,
        showCancel: true,
        onResolve: resolve,
      });
    });

  const closeImportPreview = () => {
    setImportPreviewOpen(false);
    setPendingImportFile(null);
    setImportPreviewData({
      duplicates: [],
      failed: [],
      parseWarnings: [],
      summary: {},
    });
    setDuplicateDecisions({});
    setPreviewStep(1);
    setExpandedDupKeys({});
  };

  const commitImportWithDecisions = async (file, decisions = {}) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName ?? "");
    formData.append("mode", "commit");
    formData.append("duplicateDecisions", JSON.stringify(decisions));

    setIsImportCommitting(true);
    setLoading(true);
    try {
      const response = await apiFetch(`${BASE_URL}/employees/import-excel`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(
          result?.details || result?.error || text[language].importFailed,
        );
      }
      closeImportPreview();
      setImportResultsData(result);
      setImportResultsOpen(true);
      if (typeof refetchEmployees === "function") refetchEmployees();
    } catch (err) {
      await showAlert({
        title: text[language].importFailed,
        message: err.message,
        variant: "error",
      });
    } finally {
      setIsImportCommitting(false);
      setLoading(false);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const inputElement = event.target;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName ?? "");
    formData.append("mode", "preview");
    try {
      setIsPreparingImport(true);
      setLoading(true);
      const response = await apiFetch(`${BASE_URL}/employees/import-excel`, {
        method: "POST",
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(
          result?.details || result?.error || text[language].importFailed,
        );
      }
      const previewDuplicates = Array.isArray(result.duplicates)
        ? result.duplicates
        : [];
      const previewFailed = Array.isArray(result.failed) ? result.failed : [];
      const previewParseWarnings = Array.isArray(result.parseWarnings)
        ? result.parseWarnings
        : [];
      setPendingImportFile(file);
      setImportPreviewData({
        duplicates: previewDuplicates,
        failed: previewFailed,
        parseWarnings: previewParseWarnings,
        summary: result.summary || {},
      });
      const hasValidationErrors = previewFailed.some(
        (f) => Array.isArray(f.missingFields) && f.missingFields.length > 0,
      );
      if (
        previewDuplicates.length > 0 ||
        hasValidationErrors ||
        previewParseWarnings.length > 0
      ) {
        setDuplicateDecisions({});
        setExpandedDupKeys({});
        setPreviewStep(
          hasValidationErrors || previewParseWarnings.length > 0 ? 1 : 2,
        );
        setImportPreviewOpen(true);
      } else {
        await commitImportWithDecisions(file, {});
      }
    } catch (err) {
      console.error("Import error:", err);
      await showAlert({
        message: `${text[language].serverError || "Server error"}: ${err.message}`,
      });
    } finally {
      setIsPreparingImport(false);
      setLoading(false);
      if (inputElement) inputElement.value = "";
    }
  };

  const handleOpenAdd = () => {
    setShowAddEmployee(true);
  };

  const handleExportToExcel = async () => {
    try {
      // Get current filter states from the EmployeeTable
      const filters = {
        searchTerm: "", // Will be populated from EmployeeTable filters if needed
        filterDepartment: "",
        filterStatus: "",
        filterCompany: "",
      };

      // Use hookEmployees as the data source
      const employeesToExport = Array.isArray(hookEmployees)
        ? hookEmployees
        : [];

      if (employeesToExport.length === 0) {
        await showAlert({
          title: text[language].export,
          message:
            text[language].noEmployeesToExport || "No employees to export",
          variant: "info",
        });
        return;
      }

      // Perform export
      await handleExport(employeesToExport, filters, {
        onSuccess: async (result) => {
          const format = result.format === "excel" ? "Excel" : "CSV";
          await showAlert({
            title: text[language].export,
            message: `${text[language].exportSuccess || "Successfully exported"} ${result.count} ${text[language].employees || "employees"} ${language === "ar" ? "إلى" : "to"} ${format}`,
            variant: "success",
          });
        },
        onError: async (err) => {
          await showAlert({
            title: text[language].export,
            message: `${text[language].failedToExport || "Failed to export"}: ${err.message}`,
            variant: "error",
          });
        },
      });
    } catch (err) {
      console.error("Export error:", err);
      await showAlert({
        title: text[language].export,
        message:
          err.message ||
          text[language].failedToExport ||
          "Failed to export employees",
        variant: "error",
      });
    }
  };

  // Archive handlers
  const getStatusBadge = (status) => {
    // Base styles for all status badges
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      fontSize: "0.875rem",
      fontWeight: "600",
    };

    // Return styled span based on status
    switch (status) {
      case "Active":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#d1fae5",
              color: "#065f46",
            }}
          >
            {text[language].statusActive}
          </span>
        );
      case "OnLeave":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fef3c7",
              color: "#92400e",
            }}
          >
            {text[language].statusOnLeave}
          </span>
        );
      case "Inactive":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {text[language].statusInactive}
          </span>
        );
      case "Archived":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#6b7280",
            }}
          >
            {text[language].statusArchived}
          </span>
        );
      default:
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#374151",
            }}
          >
            {text[language].statusUnknown}
          </span>
        );
    }
  };

  const handleOpenArchive = async () => {
    setShowArchive(true);
    await fetchArchivedEmployees();
  };

  const handleOpenExportTemplate = async () => {
    setShowExportTemplate(true);
  };

  const handleExportTemplate = async () => {
    try {
      setExportTemplateLoading(true);

      // Fetch the actual template file (templateFile is the webpack-processed URL)
      const response = await fetch(templateFile);

      if (!response.ok) {
        throw new Error("Failed to load template file");
      }

      const blob = await response.blob();
      return blob;
    } catch (err) {
      console.error("Failed to export template:", err);
      await showAlert({
        title: text[language].exportTemplate,
        message:
          err.message ||
          text[language].failedToExport ||
          "Failed to export template",
        variant: "error",
      });
      throw err;
    } finally {
      setExportTemplateLoading(false);
    }
  };

  const handleRestoreEmployee = async (employeeId) => {
    const confirmed = await showConfirm({
      title: text[language].confirmRestoree || text[language].restore,
      message:
        text[language].confirmRestore ||
        "Are you sure you want to restore this employee?",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await restoreEmployee(employeeId);

      if (result.success) {
        await showAlert({
          title: text[language].restoreEmployee,
          message:
            text[language].employeeRestored || "Employee restored successfully",
          variant: "success",
        });
        // Refresh main employee list
        if (typeof refetchEmployees === "function") refetchEmployees();
      } else {
        throw new Error(result.error || "Failed to restore employee");
      }
    } catch (err) {
      await showAlert({
        title: text[language].restoreEmployee || "Restore",
        message:
          err.message ||
          text[language].failedToRestore ||
          "Failed to restore employee",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreAllArchivedEmployees = async () => {
    const confirmed = await showConfirm({
      title: text[language].confirmRestoree || text[language].restoreAll,
      message: `${text[language].confirmRestoreAll || "Are you sure you want to restore all"} ${archivedCount} ${text[language].archivedEmployeesQuestion || "archived employees?"}`,
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await restoreAllEmployees();

      await showAlert({
        title: text[language].restoreAll,
        message: `${text[language].restored || "Restored"} ${result.successCount} ${text[language].employees || "employees"}${result.failCount > 0 ? `. ${result.failCount} failed.` : ""}`,
        variant: result.failCount > 0 ? "warning" : "success",
      });

      // Refresh main employee list
      if (typeof refetchEmployees === "function") refetchEmployees();
    } catch (err) {
      await showAlert({
        title: text[language].restoreAll || "Restore All",
        message:
          err.message ||
          text[language].failedToRestore ||
          "Failed to restore employees",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllArchived = async () => {
    const confirmed = await showConfirm({
      title: text[language].deleteAll || "Delete All",
      message: `${text[language].confirmDeleteAll || "Are you sure you want to permanently delete all"} ${archivedCount} ${text[language].archivedEmployeesQuestion || "archived employees?"}${text[language].cannotBeUndone || "This action cannot be undone."}`,
      variant: "error",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await deleteAllArchived();

      if (result.success) {
        await showAlert({
          title: text[language].deleteAll,
          message:
            text[language].allEmployeesDeleted ||
            "All archived employees permanently deleted!",
          variant: "success",
        });
      } else {
        throw new Error(result.error || "Failed to delete archived employees");
      }
    } catch (err) {
      await showAlert({
        title: text[language].deleteAll || "Delete All",
        message: err.message || "Failed to delete archived employees",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportArchive = async () => {
    try {
      // Use filtered archived employees for export
      const employeesToExport = filteredArchivedEmployees;

      if (employeesToExport.length === 0) {
        await showAlert({
          title: text[language].exportArchive || "Export Archive",
          message:
            text[language].noEmployeesToExport || "No employees to export",
          variant: "info",
        });
        return;
      }

      // Perform export
      await handleExport(
        employeesToExport,
        {},
        {
          onSuccess: async (result) => {
            const format = result.format === "excel" ? "Excel" : "CSV";
            await showAlert({
              title: text[language].exportArchive || "Export Archive",
              message: `${text[language].exportSuccess || "Successfully exported"} ${result.count} ${text[language].archivedEmployees || "archived employees"} ${language === "ar" ? "إلى" : "to"} ${format}`,
              variant: "success",
            });
          },
          onError: async (err) => {
            await showAlert({
              title: text[language].exportArchive || "Export Archive",
              message: `${text[language].failedToExport || "Failed to export"}: ${err.message}`,
              variant: "error",
            });
          },
        },
      );
    } catch (err) {
      console.error("Export archive error:", err);
      await showAlert({
        title: text[language].exportArchive || "Export Archive",
        message:
          err.message ||
          text[language].failedToExport ||
          "Failed to export archived employees",
        variant: "error",
      });
    }
  };

  // Helper function to get employee initials
  const getUserInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  // Action handlers for employee table
  const handleViewEmployee = (employee) => {
    setSelectedEmployeeForView(employee);
    setShowViewEmployee(true);
  };

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEditEmployee(true);
  };

const handleUpdateEmployee = async (employeeData, uploadedFiles = []) => {
  try {
    setLoading(true);

    const payload = {
      sr: employeeData.name,
      arabicName: employeeData.arabicName || "",
      email: employeeData.email,
      phoneNumber: employeeData.phone,
      employeeType: employeeData.department,
      laborMode: employeeData.laborMode || "",
      clientName:
        profile?.clientName || employeeData.company || "",
      employeeState: employeeData.status,
      netSalary: employeeData.salary ? parseFloat(employeeData.salary) : 0,
      nationalId: employeeData.nationalId || "",
      address: employeeData.address || "",
      city: employeeData.city || "",
      country: employeeData.country || "Egypt",
      nationality: employeeData.nationality || "",
      gender: employeeData.gender || "",
      maritalStatus: employeeData.maritalStatus || "",
      dob: employeeData.dateOfBirth
        ? new Date(employeeData.dateOfBirth).toISOString()
        : null,
      arabicAddress: employeeData.arabicAddress || "",
      transportationAllowances: employeeData.transportationAllowances
        ? parseFloat(employeeData.transportationAllowances)
        : 0,
      variableSalary: employeeData.variableSalary
        ? parseFloat(employeeData.variableSalary)
        : 0,
      workingCompany: employeeData.workingCompany || "",
    };

    // If files are attached, use FormData to send both JSON payload and files
    let response;
    if (uploadedFiles && uploadedFiles.length > 0) {
      const formData = new FormData();
      // Append the employee JSON payload as a string field
      formData.append("data", JSON.stringify(payload));
      uploadedFiles.forEach((file) => {
        formData.append("documents", file, file.name);
      });

      response = await apiFetch(
        `${BASE_URL}/employees/${selectedEmployee.id}`,
        {
          method: "PUT",
          body: formData,
          // Do NOT set Content-Type header — browser sets it with boundary for FormData
        },
      );
    } else {
      response = await apiFetch(
        `${BASE_URL}/employees/${selectedEmployee.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    }

    const responseData = await response.json();
    if (!response.ok || !responseData?.success) {
      throw new Error(
        responseData?.error ||
          responseData?.message ||
          "Failed to update employee",
      );
    }

    const updated = responseData.employee || {};
    setEmployees((prev) =>
      prev.map((emp) =>
        emp.id === selectedEmployee.id
          ? {
              ...emp,
              name: updated.sr || emp.name,
              email: updated.email || emp.email,
              phone: updated.phoneNumber || emp.phone,
              department: updated.employeeType || emp.department,
              laborMode: updated.laborMode || emp.laborMode,
              company: updated.clientName || emp.company,
              status: updated.employeeState || emp.status,
              salary: updated.netSalary ?? emp.salary,
              arabicName: updated.arabicName || emp.arabicName,
            }
          : emp,
      ),
    );

    setShowEditEmployee(false);
    setSelectedEmployee(null);

    await showAlert({
      title: text[language].updateEmployee || "Update Success",
      message:
        language === "ar"
          ? `تم تحديث بيانات الموظف بنجاح${uploadedFiles.length > 0 ? ` وتم رفع ${uploadedFiles.length} مستند` : ""}`
          : `Employee updated successfully${uploadedFiles.length > 0 ? ` and ${uploadedFiles.length} document${uploadedFiles.length > 1 ? "s" : ""} uploaded` : ""}`,
      variant: "success",
    });

    if (typeof refetchEmployees === "function") refetchEmployees();
  } catch (err) {
    console.error("Failed to update employee:", err);
    await showAlert({
      title: text[language].updateEmployee || "Update Failed",
      message:
        err.message ||
        (language === "ar"
          ? "فشل تحديث الموظف"
          : "Failed to update employee"),
      variant: "error",
    });
  } finally {
    setLoading(false);
  }
};

  const handleDeleteEmployee = (empOrId) => {
    (async () => {
      // Accept either an employee object or a bare ID
      const id = empOrId && typeof empOrId === "object" ? empOrId.id : empOrId;
      try {
        const confirmed = await showConfirm({
          title: text[language].deleteEmployee || "Delete",
          message:
            text[language].confirmDelete ||
            "Are you sure you want to delete this employee?",
          variant: "error",
        });
        if (!confirmed) return;
        setLoading(true);
        const response = await apiFetch(`${BASE_URL}/employees/${id}`, {
          method: "DELETE",
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data?.error || data?.message || "Failed to delete employee",
          );
        }
        // close edit modal if it was open for this employee
        setShowEditEmployee(false);
        setSelectedEmployee(null);
        await showAlert({
          title: text[language].deleteEmployee,
          message:
            text[language].employeeDeleted || "Employee deleted successfully",
          variant: "success",
        });
        // Reload data from server to reflect deletion
        if (typeof refetchEmployees === "function") refetchEmployees();
      } catch (err) {
        console.error("Failed to delete employee:", err);
        await showAlert({
          title: text[language].deleteEmployee || "Delete",
          message:
            err.message ||
            (language === "ar"
              ? "فشل حذف الموظف"
              : "Failed to delete employee"),
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleViewDocuments = (employee) => {
    loadEmployeeDocuments(employee);
  };

  const handleSaveDocuments = async () => {
    if (!selectedEmployeeDocuments?.id) return;
    try {
      await saveDocuments(selectedEmployeeDocuments.id, setEmployees);
      await showAlert({
        message: language === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
        variant: "success",
      });
    } catch (err) {
      await showAlert({
        message:
          language === "ar"
            ? `فشل الحفظ: ${err.message}`
            : `Failed to save: ${err.message}`,
        variant: "error",
      });
    }
  };

  const handleAddEmployeeSuccess = (data) => {
    console.log("Employee added successfully:", data);
    // Refresh employee list via hook instead of full page reload
    try {
      if (typeof refetchEmployees === "function") {
        refetchEmployees();
      }
    } catch (e) {
      console.error("Failed to refetch employees after add:", e);
      // fallback to full reload if necessary
      window.location.reload();
    }
    // close modal
    setShowAddEmployee(false);
  };

  const handleAddEmployee = async (employeeData) => {
    try {
      setLoading(true);

      // ──────────────────────────────────────────────
      // Sanitize empty strings and invalid values
      // ──────────────────────────────────────────────
      const sanitize = (data) => {
        const copy = { ...(data || {}) };
        Object.keys(copy).forEach((key) => {
          if (copy[key] === "" || copy[key] === undefined) {
            copy[key] = null;
          }
        });
        return copy;
      };

      const sanitized = sanitize(employeeData);

      // ──────────────────────────────────────────────
      // Resolve client information from localStorage or clientsMap
      // ──────────────────────────────────────────────
      const resolvedClientName =
        profile?.clientName ||
        sanitized.workingCompany ||
        sanitized.company ||
        (companies && companies[0]) ||
        null;

      let resolvedClientCode = null;
      const localClientCode = profile?.clientCode ?? null;

      if (localClientCode && String(localClientCode).trim() !== "") {
        const num = Number(localClientCode);
        resolvedClientCode = Number.isNaN(num) ? localClientCode : num;
      } else if (sanitized.clientCode) {
        resolvedClientCode = sanitized.clientCode;
      } else {
        // Try to find client code from clientsMap or clientsList
        const tryKeys = [
          resolvedClientName,
          sanitized.workingCompany,
          sanitized.company,
        ].filter(Boolean);
        const normalize = (s) =>
          (s || "").toString().trim().toLowerCase().replace(/\s+/g, "");

        for (const key of tryKeys) {
          const keyNorm = normalize(key);
          if (!keyNorm) continue;

          // Try direct map lookup
          if (clientsMap && clientsMap[keyNorm] !== undefined) {
            const found = clientsMap[keyNorm];
            const num = Number(found);
            resolvedClientCode = Number.isNaN(num) ? found : num;
            break;
          }

          // Try matching in clientsList
          if (Array.isArray(clientsList) && clientsList.length) {
            const match = clientsList.find((c) => {
              const cn = normalize(c.name);
              return (
                cn === keyNorm || cn.includes(keyNorm) || keyNorm.includes(cn)
              );
            });
            if (match) {
              const num = Number(match.code);
              resolvedClientCode = Number.isNaN(num) ? match.code : num;
              break;
            }
          }
        }
      }

      // Add client info to data
      sanitized.clientName = resolvedClientName;
      sanitized.clientCode = resolvedClientCode;

      console.log("📤 Form data before mapping:", sanitized);

      // ──────────────────────────────────────────────
      // Map form fields to database schema and apply transformations
      // ──────────────────────────────────────────────
      const payload = mapFormDataToDatabase(sanitized);

      console.log("📤 Database payload after mapping:", payload);

      // ──────────────────────────────────────────────
      // Call API and handle response
      // ──────────────────────────────────────────────
      const data = await addEmployeeAPI(sanitized);

      console.log("✅ Employee added successfully:", data);
      handleAddEmployeeSuccess(data);

      // Show success alert
      setAlertState({
        open: true,
        title: text[language].employeeAdded || "Success",
        message:
          text[language].employeeAddedMessage || "Employee added successfully",
        variant: "success",
        showCancel: false,
      });

      return data;
    } catch (err) {
      console.error("❌ Error adding employee:", err);

      const errorMessage =
        err.message || text[language].failedToAdd || "Failed to add employee";

      setAlertState({
        open: true,
        title: text[language].failedToAdd || "Error",
        message: errorMessage,
        variant: "error",
        showCancel: false,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Stats data for StatsGrid
  const stats = [
    {
      title: text[language].totalEmployees,
      value: employeeCount,
      icon: icons.Users,
      iconBackground: "rgb(0, 180, 72)",
      showSymbol: true,
      valueAlign: 50,
    },
    {
      title: text[language].activeEmployees,
      value: counts.Active,
      description: text[language].thisWeek,
      icon: icons.UserCheck,
      iconBackground: "rgb(6, 0, 180)",
      valueAlign: 50,
    },
    {
      title: text[language].onLeave,
      value: counts.OnLeave,
      description: text[language].today,
      icon: icons.Calendar,
      iconBackground: "rgb(180, 0, 72)",
      valueAlign: 50,
    },
    {
      title: text[language].newEmployees,
      value: counts.newEmployees,
      description: text[language].ovearall,
      icon: icons.UserPlus,
      iconBackground: "rgb(180, 57, 0)",
      valueAlign: 50,
    },
    {
      title: text[language].archived,
      value: counts.archived,
      description: text[language].ovearall,
      icon: icons.Building,
      iconBackground: "rgb(96, 96, 96)",
      valueAlign: 50,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Welcome Section (use WelcomeCard for consistency) */}
      <WelcomeCard
        styles={styles}
        text={text}
        language={language}
        userName={
          language === "ar"
            ? localStorage.getItem("employeeArabicName")?.split(" ")[0] || ""
            : localStorage.getItem("employeeName")?.split(" ")[0] || ""
        }
        title={{ en: text.en.title, ar: text.ar.title }}
      />

      <StatsGrid
        stats={stats}
        styles={styles}
        gridStyle={{
          gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
          //gap: styles?.statsGrid?.gap || "20px",
          //overflowX: "auto",
        }}
      />

      <EmptyCard
        styles={styles}
        // place directly under stats grid with the same bottom margin as the grid
        containerStyle={{
          marginTop: styles?.statsGrid?.marginBottom || "24px",
        }}
        // align title left and actions to the right (you can change to 'center')
        actionsAlign="flex-start"
        hideTitle={true}
      >
        <Button
          bgColor="#2e7cba"
          color="#fff"
          borderRadius="10px"
          onClick={handleOpenAdd}
          style={{
            padding: "10px 18px",
            display: "inline-flex",
            gap: 10,
            alignItems: "center",
          }}
        >
          <Plus size={18} />
          {text[language].addNewEmployee}
        </Button>

        {/* Hidden file input for import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: "none" }}
          onChange={handleImportExcel}
          disabled={loading || isPreparingImport}
        />

        <Button
          bgColor={styles?.statCard?.background || "transparent"}
          color={theme === "dark" ? "#fff" : "#171616"}
          borderRadius="10px"
          background="#5555"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading || isPreparingImport}
          style={{
            padding: "10px 18px",
            display: "inline-flex",
            gap: 10,
            background: "#1b1b1b13",
            alignItems: "center",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: loading || isPreparingImport ? 0.7 : 1,
          }}
        >
          <Upload size={16} />
          {isPreparingImport
            ? text[language].processingImport || "Processing..."
            : text[language].import}
        </Button>

        <Button
          bgColor={styles?.statCard?.background || "transparent"}
          color={theme === "dark" ? "#fff" : "#171616"}
          borderRadius="10px"
          onClick={handleExportToExcel}
          disabled={loading || isExporting}
          style={{
            padding: "10px 18px",
            display: "inline-flex",
            gap: 10,
            alignItems: "center",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: loading || isExporting ? 0.7 : 1,
            cursor: loading || isExporting ? "not-allowed" : "pointer",
            background: "#1b1b1b13",
          }}
        >
          <Download size={16} />
          {isExporting
            ? text[language].exporting || "Exporting..."
            : text[language].export}
        </Button>

        <Button
          bgColor={styles?.statCard?.background || "transparent"}
          color={theme === "dark" ? "#fff" : "#171616"}
          borderRadius="10px"
          onClick={handleOpenArchive}
          disabled={loading || archiveLoading}
          style={{
            padding: "10px 18px",
            background: "#1b1b1b13",

            display: "inline-flex",
            gap: 10,
            alignItems: "center",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: loading || archiveLoading ? 0.7 : 1,
            cursor: loading || archiveLoading ? "not-allowed" : "pointer",
          }}
        >
          <Archive size={16} />
          {`${text[language].archived} (${archivedCount || counts.archived || 0})`}
        </Button>

        <Button
          bgColor={styles?.statCard?.background || "transparent"}
          color={theme === "dark" ? "#fff" : "#171616"}
          borderRadius="10px"
          onClick={handleOpenExportTemplate}
          disabled={loading}
          style={{
            padding: "10px 18px",
            display: "inline-flex",
            background: "#1b1b1b13",

            gap: 10,
            alignItems: "center",
            border: "1px solid rgba(255,255,255,0.06)",
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          <FileText size={16} />
          {text[language].exportTemplate}
        </Button>
      </EmptyCard>

      
      
      <EmptyCard
        styles={styles}
        containerStyle={{
          marginTop: "0px",
          marginBottom: "20px",
          padding: "0px",
          //border: "none",
          //background: "transparent",
        }}
        actionsAlign="flex-start"
        hideTitle={true}
      >
        {/* Employee Table */}
        <EmployeeTable
          onViewEmployee={handleViewEmployee}
          onEditEmployee={handleEditEmployee}
          onDeleteEmployee={handleDeleteEmployee}
          onViewDocuments={handleViewDocuments}
        />
      </EmptyCard>

      {/* Alert modal for errors/success */}
      <AlertModal
        open={alertState.open}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        showCancel={alertState.showCancel}
        onConfirm={() => {
          alertState.onResolve?.(true);
          setAlertState((s) => ({ ...s, open: false }));
        }}
        onClose={() => {
          alertState.onResolve?.(false);
          setAlertState((s) => ({ ...s, open: false }));
        }}
        theme={theme}
        language={language}
      />

      {/* View Employee Modal */}
      {showViewEmployee && selectedEmployeeForView && (
        <ViewEmployeeModal
          selectedEmployee={selectedEmployeeForView}
          setShowViewEmployee={setShowViewEmployee}
          text={text}
          language={language}
          screenSize={screenSize}
          spacing={{ margin: 12 }}
          employeeProfilePics={employeeProfilePics}
          getUserInitials={getUserInitials}
          getStatusBadge={getStatusBadge}
          theme={theme}
        />
      )}

      {/* Edit Employee Modal */}
      {showEditEmployee && selectedEmployee && (
        <EditEmployeeModal
          selectedEmployee={selectedEmployee}
          setShowEditEmployee={setShowEditEmployee}
          setSelectedEmployee={setSelectedEmployee}
          language={language}
          text={text}
          unifiedControlStyle={unifiedControlStyle}
          departments={departmentsList}
          ReusableDropdown={ReusableDropdown}
          handleUpdateEmployee={handleUpdateEmployee}
          handleDeleteEmployee={handleDeleteEmployee}
          clientName={profile?.clientName}
          theme={theme}
          screenSize={screenSize}
          loading={loading}
        />
      )}

      {showViewEmployee && (
        <ViewEmployeeModal
          selectedEmployee={selectedEmployee}
          setShowViewEmployee={setShowViewEmployee}
          text={text}
          language={language}
          screenSize={screenSize}
          spacing={{ margin: 12 }}
          employeeProfilePics={employeeProfilePics}
          getUserInitials={getUserInitials}
          getStatusBadge={getStatusBadge}
          theme={theme}
        />
      )}
      {/* Documents Modal */}
      {showDocumentsModal && (
        <DocumentsModal
          selectedEmployeeDocuments={selectedEmployeeDocuments}
          setShowDocumentsModal={setShowDocumentsModal}
          setSelectedEmployeeDocuments={setSelectedEmployeeDocuments}
          apiFetch={apiFetch}
          API_URL={BASE_URL}
          showAlert={showAlert}
          language={language}
          screenSize={screenSize}
          text={text}
          loadingDocuments={loadingDocuments}
          theme={theme}
          spacing={{ margin: "1.5rem", gap: "1rem", padding: "1rem" }}
          fonts={{ body: "14px", small: "12px", subtitle: "16px" }}
          documentChecklist={documentChecklist}
          handleDocumentCheckboxChange={updateDocumentCheckbox}
          handleSaveDocuments={handleSaveDocuments}
          hasUnsavedChanges={hasUnsavedChanges}
          savingDocument={savingDocument}
        />
      )}

      {showAddEmployee && (
        <AddEmployeeModal
          styles={styles}
          departments={departmentsList}
          companies={companies}
          departmentsLoading={employeesLoading}
          loading={loading}
          screenSize={screenSize}
          onClose={() => setShowAddEmployee(false)}
          onSubmit={handleAddEmployee}
        />
      )}

      {/* Import Preview Modal */}
      {importPreviewOpen && (
        <ImportPreviewModal
          importPreviewData={importPreviewData}
          onClose={closeImportPreview}
          pendingImportFile={pendingImportFile}
          duplicateDecisions={duplicateDecisions}
          setDuplicateDecisions={setDuplicateDecisions}
          previewStep={previewStep}
          setPreviewStep={setPreviewStep}
          expandedDupKeys={expandedDupKeys}
          setExpandedDupKeys={setExpandedDupKeys}
          isImportCommitting={isImportCommitting}
          isPreparingImport={isPreparingImport}
          commitImportWithDecisions={commitImportWithDecisions}
          theme={theme}
          language={language}
          screenSize={screenSize}
          text={text}
          showAlert={showAlert}
        />
      )}

      {/* Import Results Modal */}
      {importResultsOpen && (
        <ImportResultsModal
          importResultsData={importResultsData}
          setImportResultsOpen={setImportResultsOpen}
          styles={styles}
          fonts={{ body: "1rem", small: "0.875rem", tiny: "0.8125rem" }}
          theme={theme}
          language={language}
          screenSize={screenSize}
        />
      )}

      {/* Archive Modal */}
      {showArchive && (
        <ArchiveModal
          archivedEmployees={archivedEmployees}
          archivedCount={archivedCount}
          setShowArchive={setShowArchive}
          archiveSearch={archiveSearch}
          setArchiveSearch={setArchiveSearch}
          archiveFilterCompany={archiveFilterCompany}
          setArchiveFilterCompany={setArchiveFilterCompany}
          archiveFilterCompanySearch={archiveFilterCompanySearch}
          setArchiveFilterCompanySearch={setArchiveFilterCompanySearch}
          archiveFilterWorkingCompany={archiveFilterWorkingCompany}
          setArchiveFilterWorkingCompany={setArchiveFilterWorkingCompany}
          archiveFilterWorkingCompanySearch={archiveFilterWorkingCompanySearch}
          setArchiveFilterWorkingCompanySearch={
            setArchiveFilterWorkingCompanySearch
          }
          archiveFilterDepartment={archiveFilterDepartment}
          setArchiveFilterDepartment={setArchiveFilterDepartment}
          archiveFilterDepartmentSearch={archiveFilterDepartmentSearch}
          setArchiveFilterDepartmentSearch={setArchiveFilterDepartmentSearch}
          archiveFilterDateFrom={archiveFilterDateFrom}
          setArchiveFilterDateFrom={setArchiveFilterDateFrom}
          archiveFilterDateTo={archiveFilterDateTo}
          setArchiveFilterDateTo={setArchiveFilterDateTo}
          filteredArchivedEmployees={filteredArchivedEmployees}
          filterOptions={filterOptions}
          archivedThisMonth={archivedThisMonth}
          isAdminRole={true}
          loading={loading || archiveLoading}
          handleExportArchive={handleExportArchive}
          handleRestoreEmployee={handleRestoreEmployee}
          handleDeleteEmployee={handleDeleteEmployee}
          handleRestoreAllArchivedEmployees={handleRestoreAllArchivedEmployees}
          handleDeleteAllArchived={handleDeleteAllArchived}
          clearArchiveFilters={clearArchiveFilters}
          getStatusBadge={getStatusBadge}
          theme={theme}
          language={language}
          screenSize={screenSize}
          text={text}
          showAlert={showAlert}
          showConfirm={showConfirm}
          ReusableDropdown={ReusableDropdown}
          canDeleteEmployee={true}
        />
      )}

      {/* Export Template Modal */}
      <ExportTemplateModal
        isOpen={showExportTemplate}
        onClose={() => setShowExportTemplate(false)}
        fileName="employee-template.xlsx"
        title={text[language].exportTemplate}
        description={
          language === "ar"
            ? "تحميل قالب الموظفين للاستيراد الجماعي"
            : "Download the employee template for bulk import operations"
        }
        successMessage={text[language].templateExported}
        theme={theme}
        language={language}
        screenSize={screenSize}
        isLoading={exportTemplateLoading}
        onExport={handleExportTemplate}
      />
    </div>
  );
};

export default Dashboard;
